import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { API_BASE_URL } from '../../config';
import './AdminSystem.css';

interface MetricPoint {
  time: string;
  value: number;
}

interface MaxPoint {
  value: number;
  time: string;
}

interface SystemMetrics {
  current: {
    db_connections: number | null;
    db_max_connections: number | null;
    db_tier: string | null;
  };
  timeseries: {
    db_connections: MetricPoint[];
    db_cpu: MetricPoint[];
    db_memory: MetricPoint[];
    db_disk: MetricPoint[];
    api_requests: MetricPoint[];
    api_instances: MetricPoint[];
  };
  monthly_max?: {
    db_connections?: MaxPoint;
    db_cpu?: MaxPoint;
    db_memory?: MaxPoint;
    db_disk?: MaxPoint;
    api_instances?: MaxPoint;
  };
}

const lastValue = (points: MetricPoint[]): number =>
  points.length > 0 ? points[points.length - 1].value : 0;

const REFRESH_INTERVAL = 60000;

const formatTime = (time: string) => {
  const d = new Date(time);
  const day = d.getDate().toString().padStart(2, '0');
  const mon = (d.getMonth() + 1).toString().padStart(2, '0');
  const hour = d.getHours().toString().padStart(2, '0');
  return `${day}/${mon} ${hour}:00`;
};

const formatMaxTime = (time: string) => {
  const d = new Date(time);
  const day = d.getDate().toString().padStart(2, '0');
  const mon = (d.getMonth() + 1).toString().padStart(2, '0');
  const hour = d.getHours().toString().padStart(2, '0');
  const min = d.getMinutes().toString().padStart(2, '0');
  return `${day}/${mon} ${hour}:${min}`;
};

const getConnectionColor = (current: number, max: number): string => {
  const ratio = current / max;
  if (ratio < 0.5) return '#4a4a4a';
  if (ratio < 0.8) return '#8a7a00';
  return '#a33';
};

const getConnectionBgColor = (current: number, max: number): string => {
  const ratio = current / max;
  if (ratio < 0.5) return '#e8f5e9';
  if (ratio < 0.8) return '#fff8e1';
  return '#ffebee';
};

const AdminSystem: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/system/metrics`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMetrics(data);
      setError(null);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.message || 'Błąd pobierania metryk');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  if (loading) {
    return (
      <div className="admin-system">
        <div className="admin-system-loading">
          <div className="admin-system-spinner" />
          <span>Ładowanie metryk systemowych...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-system">
        <div className="admin-system-error">
          <span className="admin-system-error-icon">!</span>
          <span>{error}</span>
          <button className="admin-system-retry" onClick={fetchMetrics}>Spróbuj ponownie</button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const { current, timeseries, monthly_max } = metrics;
  const dbConn = current.db_connections ?? 0;
  const dbMax = current.db_max_connections ?? 25;
  const dbTier = current.db_tier ?? 'unknown';
  const dbCpu = lastValue(timeseries.db_cpu) * 100;
  const dbMem = lastValue(timeseries.db_memory) * 100;
  const apiInst = lastValue(timeseries.api_instances);

  const toPercent = (points: MetricPoint[]) =>
    points.map(p => ({ ...p, value: p.value * 100 }));
  const connColor = getConnectionColor(dbConn, dbMax);
  const connBg = getConnectionBgColor(dbConn, dbMax);

  const maxConn = monthly_max?.db_connections;
  const maxCpu = monthly_max?.db_cpu;
  const maxMem = monthly_max?.db_memory;
  const maxDisk = monthly_max?.db_disk;
  const maxInst = monthly_max?.api_instances;

  const chartProps = {
    margin: { top: 8, right: 12, left: -10, bottom: 0 },
  };

  const gridProps = {
    strokeDasharray: '3 3',
    stroke: '#e0e0e0',
  };

  const xAxisProps = {
    dataKey: 'time',
    tickFormatter: formatTime,
    tick: { fontSize: 10, fill: '#999' },
    axisLine: { stroke: '#e0e0e0' },
    tickLine: false,
  };

  const yAxisProps = {
    tick: { fontSize: 11, fill: '#999' },
    axisLine: false,
    tickLine: false,
  };

  const tooltipProps = {
    contentStyle: {
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 6,
      fontSize: 12,
    },
    labelFormatter: (label: any) => formatTime(String(label)),
  };

  return (
    <div className="admin-system">
      <div className="admin-system-header">
        <h2>System Metrics</h2>
        {lastUpdated && (
          <span className="admin-system-updated">
            Ostatnia aktualizacja: {lastUpdated.toLocaleTimeString('pl-PL')}
          </span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="admin-system-cards">
        <div className="admin-system-card" style={{ borderLeftColor: connColor }}>
          <div className="admin-system-card-label">DB Connections</div>
          <div className="admin-system-card-value">
            <span style={{ color: connColor }}>{dbConn}</span>
            <span className="admin-system-card-max"> / {dbMax}</span>
          </div>
          <div className="admin-system-card-bar" style={{ background: '#eee' }}>
            <div
              className="admin-system-card-bar-fill"
              style={{
                width: `${Math.min(100, (dbConn / dbMax) * 100)}%`,
                background: connBg,
                borderRight: `2px solid ${connColor}`,
              }}
            />
          </div>
          {maxConn && (
            <div className="admin-system-card-max-info">
              Max 30d: {Math.round(maxConn.value)} ({formatMaxTime(maxConn.time)})
            </div>
          )}
        </div>

        <div className="admin-system-card" style={{ borderLeftColor: '#666' }}>
          <div className="admin-system-card-label">DB Tier</div>
          <div className="admin-system-card-value">{dbTier}</div>
        </div>

        <div className="admin-system-card" style={{ borderLeftColor: '#666' }}>
          <div className="admin-system-card-label">DB CPU</div>
          <div className="admin-system-card-value">{dbCpu.toFixed(1)}%</div>
          {maxCpu && (
            <div className="admin-system-card-max-info">
              Max 30d: {(maxCpu.value * 100).toFixed(1)}% ({formatMaxTime(maxCpu.time)})
            </div>
          )}
        </div>

        <div className="admin-system-card" style={{ borderLeftColor: '#666' }}>
          <div className="admin-system-card-label">DB Memory</div>
          <div className="admin-system-card-value">{dbMem.toFixed(1)}%</div>
          {maxMem && (
            <div className="admin-system-card-max-info">
              Max 30d: {(maxMem.value * 100).toFixed(1)}% ({formatMaxTime(maxMem.time)})
            </div>
          )}
        </div>

        <div className="admin-system-card" style={{ borderLeftColor: '#666' }}>
          <div className="admin-system-card-label">API Instances</div>
          <div className="admin-system-card-value">{Math.round(apiInst)}</div>
          {maxInst && (
            <div className="admin-system-card-max-info">
              Max 30d: {Math.round(maxInst.value)} ({formatMaxTime(maxInst.time)})
            </div>
          )}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="admin-system-charts">
        <div className="admin-system-chart-card">
          <h3>DB Connections (7d)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={timeseries.db_connections} {...chartProps}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip {...tooltipProps} />
              <Area type="monotone" dataKey="value" stroke="#333" fill="#e8e8e8" strokeWidth={1.5} name="Connections" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-system-chart-card">
          <h3>DB CPU Usage % (7d)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={toPercent(timeseries.db_cpu)} {...chartProps}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} domain={[0, 100]} />
              <Tooltip {...tooltipProps} />
              <Line type="monotone" dataKey="value" stroke="#333" strokeWidth={1.5} dot={false} name="CPU %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-system-chart-card">
          <h3>DB Memory Usage % (7d)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={toPercent(timeseries.db_memory)} {...chartProps}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} domain={[0, 100]} />
              <Tooltip {...tooltipProps} />
              <Line type="monotone" dataKey="value" stroke="#333" strokeWidth={1.5} dot={false} name="Memory %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-system-chart-card">
          <h3>DB Disk Usage % (7d)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={toPercent(timeseries.db_disk)} {...chartProps}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} domain={[0, 100]} />
              <Tooltip {...tooltipProps} />
              <Line type="monotone" dataKey="value" stroke="#333" strokeWidth={1.5} dot={false} name="Disk %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-system-chart-card">
          <h3>API Requests (7d)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={timeseries.api_requests} {...chartProps}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="value" fill="#999" radius={[2, 2, 0, 0]} name="Requests" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-system-chart-card">
          <h3>API Instances (7d)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeseries.api_instances} {...chartProps}>
              <CartesianGrid {...gridProps} />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} allowDecimals={false} />
              <Tooltip {...tooltipProps} />
              <Line type="stepAfter" dataKey="value" stroke="#333" strokeWidth={1.5} dot={false} name="Instances" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminSystem;
